// The account's own security surface, routed at `/security`.
//
// Three things live here and they are deliberately in this order: the passkeys
// the account signs in with, the history of what has happened to the account,
// and — last, because it is the only destructive control on the page — sign out
// everywhere.
//
// Everything on this page is *account*-scoped, so no organization role applies
// and no API key can reach any of it: `01` §4.4.7 rows 57-60 make
// `/api/cloud/auth/{passkeys,verify,sign-out-all,events}` session-only, which is
// why a leaked CI credential cannot delete the human's authentication factors.

import { LitElement, css, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import "../components/index.js";
import { toast, type BnColumn, type BnKvRow, type BnRow } from "../components/index.js";

import { listApiKeys, revokeApiKey, type ApiKey } from "../api/apiKeys.js";
import {
  deletePasskey,
  listPasskeys,
  listSecurityEvents,
  registerPasskey,
  renamePasskey,
  signOut,
  signOutEverywhere,
  type Passkey,
  type SecurityEvent,
} from "../api/auth.js";
import { ApiError, parseError } from "../api/errors.js";
import { devicePasskeyName, passkeyFailureMessage, webauthnSupported } from "../api/webauthn.js";
import { navigate } from "../nav.js";

/** The longest name the passkey route accepts (`01` §1.3, "text ≤ 100"). */
const NAME_LIMIT = 100;

/** How many history rows a page carries (`SC-S013`: "the most recent 20"). */
const EVENT_PAGE = 20;

/**
 * EVENT_LABELS is the human copy for the `auth_events` vocabulary of `01` §8.3,
 * plus the alternative spellings `api/auth.ts` documents for the same events.
 * The two spellings coexist on purpose: the Go handler does not exist yet, so
 * whichever the server emits renders as a sentence, and `SC-S013` requires an
 * event type this table does not know to render as its **raw string** rather
 * than an empty cell — a new server-side event type must never need a client
 * release.
 *
 * Nothing here is derived from `meta`. `01` §8.4 makes "meta never carries
 * secrets" a package rule, and this feed states only *what* happened and from
 * where; there is no code, token, key prefix or credential material to show and
 * none is rendered.
 */
const EVENT_LABELS: Record<string, string> = {
  // Sign-in and session.
  otp_requested: "Sign-in code requested",
  login_otp: "Signed in with an email code",
  login_passkey: "Signed in with a passkey",
  login_oauth2: "Signed in with single sign-on",
  login_failed: "Sign-in attempt failed",
  sign_in: "Signed in",
  signout_all: "Signed out everywhere",
  sign_out_all: "Signed out everywhere",
  // Credentials.
  passkey_added: "Passkey added",
  passkey_removed: "Passkey removed",
  passkey_renamed: "Passkey renamed",
  stepup_verified: "Identity verified for a sensitive action",
  step_up_verified: "Identity verified for a sensitive action",
  apikey_created: "API key created",
  api_key_created: "API key created",
  apikey_revoked: "API key revoked",
  api_key_revoked: "API key revoked",
  apikey_exchanged: "API key exchanged for a short-lived token",
  apikey_used_first: "API key used for the first time",
  // Organization and instance activity that names this account as the actor.
  member_added: "Added to an organization",
  member_removed: "Removed from an organization",
  member_role_changed: "Organization role changed",
  invite_created: "Invitation sent",
  invite_accepted: "Invitation accepted",
  invite_revoked: "Invitation revoked",
  org_created: "Organization created",
  org_renamed: "Organization renamed",
  org_deleted: "Organization deleted",
  instance_created: "Instance created",
  instance_deleted: "Instance deleted",
  instance_power: "Instance restarted, suspended or resumed",
  generation_published: "Generation published",
  generation_rolled_back: "Generation rolled back",
  datasource_credential_rotated: "Datasource credential rotated",
  audit_exported: "Audit trail exported",
  subject_impersonated: "Query run as another subject",
  agent_credential_issued: "Instance agent credential issued",
  agent_credential_rotated: "Instance agent credential rotated",
  agent_credential_revoked: "Instance agent credential revoked",
};

/** METHOD_LABELS names how the caller authenticated for an event that has a
 * method at all. Membership changes arrive through model hooks and carry none. */
const METHOD_LABELS: Record<string, string> = {
  otp: "Email code",
  passkey: "Passkey",
  sso: "Single sign-on",
  api_key: "API key",
};

/**
 * page-security is the account security console (`/security`).
 *
 * It implements four features and their stories:
 *
 * - **`SC-F004` / `SC-S005`** — passkey registration. The name is prefilled from
 *   the device ("Chrome on macOS") and the 100-character limit is enforced
 *   *before* the authenticator prompt, so a rejected finish never strands a
 *   resident credential in the user's password manager.
 * - **`SC-F007` / `SC-S007`** — list, rename and delete, **including the last
 *   passkey**. That is a design decision, not an oversight: the email one-time
 *   code is always available, so a passkey-less account is never locked out, and
 *   the confirmation says so instead of warning about a lockout that cannot
 *   happen. Registering one also enables usernameless sign-in (`SC-F005`).
 * - **`SC-F010` / `SC-S010`** — sign out everywhere. The **server call happens
 *   first**; only once it confirms is the local session cleared. The dialog
 *   states plainly what the action does *not* kill — API keys survive, because
 *   the mechanism is a session-key rotation — and offers the one-click "revoke
 *   my keys too" checkbox `01` §2.1 requires, since the plausible reason to
 *   press this button is credential compromise.
 * - **`SC-F016` / `SC-S013`** — the account's own sign-in and security history,
 *   keyset-paginated, failures included. It is an audit feed and it never shows
 *   a secret: `01` §8.4 makes that a package rule, and the client half of it is
 *   to render only the event, the method, the origin and the time.
 *
 * Step-up verification (`SC-F011`) is not exercised here: none of these actions
 * is gated, because each one *reduces* authority (`01` §3.3, "deliberately not
 * gated"). Minting a credential is, and that lives on `page-api-keys`.
 *
 * The three cards load and fail independently — an outage on the history feed
 * must not hide passkey management.
 */
@customElement("page-security")
export class PageSecurity extends LitElement {
  // ── Passkeys ────────────────────────────────────────────────────────────
  @state() private passkeys: Passkey[] = [];
  @state() private pkLoading = true;
  @state() private pkError: ApiError | null = null;

  @state() private addOpen = false;
  @state() private addName = "";
  @state() private adding = false;

  @state() private renameTarget: Passkey | null = null;
  @state() private renameName = "";
  @state() private renaming = false;

  @state() private removeTarget: Passkey | null = null;
  @state() private removing = false;

  // ── Security history ────────────────────────────────────────────────────
  @state() private events: SecurityEvent[] = [];
  @state() private evLoading = true;
  @state() private evError: ApiError | null = null;
  @state() private evNext: string | null = null;
  @state() private evMore = false;

  // ── Sign out everywhere ─────────────────────────────────────────────────
  @state() private signOutOpen = false;
  @state() private signingOut = false;
  @state() private alsoRevokeKeys = false;
  @state() private keys: ApiKey[] = [];
  @state() private keysLoading = false;
  @state() private keysError: ApiError | null = null;

  /**
   * Whether this browser can run a WebAuthn ceremony at all. Static per page
   * life, and when it is false the add affordance is not rendered — `SC-S008`
   * requires the email-code path to be the only visible option rather than a
   * passkey button that cannot work.
   */
  private readonly passkeySupport = webauthnSupported();

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    h1 {
      margin: 0 0 var(--bino-space-xs);
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
    }
    .subtitle {
      margin: 0 0 var(--bino-space-lg);
      max-width: 70ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    .stack {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-lg);
    }
    .copy {
      margin: 0 0 var(--bino-space-md);
      max-width: 70ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    .copy:last-child {
      margin-bottom: 0;
    }
    .copy strong {
      color: var(--bino-text);
      font-weight: 600;
    }
    .name {
      font-family: var(--bino-font-mono);
      color: var(--bino-text);
    }
    .row-actions {
      display: flex;
      gap: var(--bino-space-xs);
      justify-content: flex-end;
    }
    .cell-event {
      display: inline-flex;
      align-items: center;
      gap: var(--bino-space-sm);
    }
    .ua {
      display: inline-block;
      max-width: 22ch;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      vertical-align: bottom;
      color: var(--bino-text-secondary);
    }
    .foot {
      display: flex;
      justify-content: center;
      padding: var(--bino-space-sm) var(--bino-space-md);
      border-top: 1px solid var(--bino-border);
    }
    .note {
      margin: 0;
      padding: var(--bino-space-md) var(--bino-space-lg);
      border-top: 1px solid var(--bino-border);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    /* The failure block: the product's copy, then the two things a support
       conversation actually needs — the catalogue code and the request id. */
    .fail {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-md);
      padding: var(--bino-space-lg);
    }
    .fail-message {
      margin: 0;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
    }
    .fail-actions {
      display: flex;
      gap: var(--bino-space-sm);
    }
    .keys-list {
      margin: 0 0 var(--bino-space-md);
      padding-left: var(--bino-space-lg);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text-secondary);
    }
    .keys-list code {
      font-family: var(--bino-font-mono);
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    void this.loadPasskeys();
    void this.loadEvents();
  }

  // ── Loading ─────────────────────────────────────────────────────────────

  private async loadPasskeys() {
    this.pkLoading = true;
    this.pkError = null;
    try {
      this.passkeys = await listPasskeys();
    } catch (err) {
      this.pkError = parseError(err);
    } finally {
      this.pkLoading = false;
    }
  }

  private async loadEvents() {
    this.evLoading = true;
    this.evError = null;
    try {
      const page = await listSecurityEvents({ limit: EVENT_PAGE });
      this.events = page.items;
      this.evNext = page.next;
    } catch (err) {
      this.evError = parseError(err);
    } finally {
      this.evLoading = false;
    }
  }

  /** loadMoreEvents appends the next keyset page. `SC-S013` requires the rows
   * already on screen to be neither re-fetched nor reordered, so this appends
   * and never replaces — and a failure toasts rather than blanking the card. */
  private async loadMoreEvents() {
    if (this.evNext === null || this.evMore) return;
    this.evMore = true;
    try {
      const page = await listSecurityEvents({ limit: EVENT_PAGE, cursor: this.evNext });
      this.events = [...this.events, ...page.items];
      this.evNext = page.next;
    } catch (err) {
      toast(this.failureText(parseError(err)), "error");
    } finally {
      this.evMore = false;
    }
  }

  // ── Passkey: add ────────────────────────────────────────────────────────

  private openAdd() {
    // A device-derived default ("Chrome on macOS") so an unedited submit still
    // yields a row the user can tell apart from the others.
    this.addName = devicePasskeyName();
    this.addOpen = true;
  }

  private closeAdd() {
    if (this.adding) return;
    this.addOpen = false;
  }

  private async submitAdd() {
    const name = this.addName.trim();
    // The length check runs here as well as in the API module because the
    // server enforces the cap only at *finish*, by which point the
    // authenticator has already minted a resident credential (`SC-S005`).
    if (name === "" || name.length > NAME_LIMIT || this.adding) return;
    this.adding = true;
    try {
      await registerPasskey(name);
      this.addOpen = false;
      toast("Passkey added", "success");
      // Reload rather than append: `created` and the backup-eligibility flag
      // come from the server's view of the finished ceremony.
      await this.loadPasskeys();
      void this.loadEvents();
    } catch (err) {
      // A dismissed prompt is a cancellation, not a failure (`SC-S008`): the
      // modal stays open, nothing is said. Everything else names its class.
      const message = passkeyFailureMessage(err);
      if (message) toast(message, "error");
    } finally {
      this.adding = false;
    }
  }

  // ── Passkey: rename ─────────────────────────────────────────────────────

  private openRename(passkey: Passkey) {
    this.renameTarget = passkey;
    this.renameName = passkey.name;
  }

  private closeRename() {
    if (this.renaming) return;
    this.renameTarget = null;
  }

  private async submitRename() {
    const target = this.renameTarget;
    const name = this.renameName.trim();
    if (!target || name === "" || name.length > NAME_LIMIT || this.renaming) return;
    this.renaming = true;
    try {
      await renamePasskey(target.id, name);
      // Patch in place: the new name appears without a page reload (`SC-S007`).
      this.passkeys = this.passkeys.map((p) => (p.id === target.id ? { ...p, name } : p));
      this.renameTarget = null;
      toast("Passkey renamed", "success");
      void this.loadEvents();
    } catch (err) {
      toast(this.failureText(parseError(err)), "error");
    } finally {
      this.renaming = false;
    }
  }

  // ── Passkey: remove ─────────────────────────────────────────────────────

  private closeRemove() {
    if (this.removing) return;
    this.removeTarget = null;
  }

  private async confirmRemove() {
    const target = this.removeTarget;
    if (!target || this.removing) return;
    this.removing = true;
    try {
      await deletePasskey(target.id);
      this.passkeys = this.passkeys.filter((p) => p.id !== target.id);
      this.removeTarget = null;
      toast("Passkey removed", "success");
      void this.loadEvents();
    } catch (err) {
      toast(this.failureText(parseError(err)), "error");
    } finally {
      this.removing = false;
    }
  }

  // ── Sign out everywhere ─────────────────────────────────────────────────

  private openSignOut() {
    this.alsoRevokeKeys = false;
    this.signOutOpen = true;
    void this.loadKeys();
  }

  private closeSignOut() {
    if (this.signingOut) return;
    this.signOutOpen = false;
  }

  /**
   * loadKeys fills the "what survives" list in the confirmation. `01` §2.1
   * deviates from bino-cloud here deliberately: the dialog names the credentials
   * that sign-out-everywhere leaves alive, because a user who believes they have
   * just revoked everything is worse off than one who knows they have not.
   */
  private async loadKeys() {
    this.keysLoading = true;
    this.keysError = null;
    try {
      const page = await listApiKeys();
      this.keys = page.items;
    } catch (err) {
      this.keysError = parseError(err);
    } finally {
      this.keysLoading = false;
    }
  }

  /**
   * confirmSignOut runs the panic button. Ordering is the whole story
   * (`SC-S010`): key revocations first when they were asked for, then the
   * server-side sign-out, then — and only then — the local store. Reversing the
   * last two signs *this* tab out while leaving every other device live if the
   * request fails, which is the exact opposite of the intent.
   */
  private async confirmSignOut() {
    if (this.signingOut) return;
    this.signingOut = true;
    try {
      if (this.alsoRevokeKeys) {
        const pending = [...this.keys];
        let revoked = 0;
        for (const key of pending) {
          try {
            await revokeApiKey(key.id);
            revoked++;
          } catch (err) {
            // Stop before signing out: the user asked for both, and completing
            // the sign-out would hide the fact that a credential is still live.
            this.keys = pending.slice(revoked);
            toast(
              `Revoked ${revoked} of ${pending.length} keys, then stopped: ${this.failureText(parseError(err))} You are still signed in.`,
              "error",
            );
            this.signingOut = false;
            return;
          }
        }
        this.keys = [];
      }
      await signOutEverywhere();
      toast("Signed out on every device", "success");
      signOut();
      navigate("/sign-in");
      // No reset in a `finally`: on success this element is being torn down and
      // re-rendering a modal nobody can see is pointless work.
    } catch (err) {
      toast(this.failureText(parseError(err)), "error");
      this.signingOut = false;
    }
  }

  // ── Formatting ──────────────────────────────────────────────────────────

  private fmtDate(iso: string): string {
    if (!iso) return "—";
    const at = new Date(iso);
    if (Number.isNaN(at.getTime())) return iso;
    return at.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }

  /** fmtWhen is `SC-S013`'s timestamp rule: relative while that is the more
   * useful reading, absolute beyond a week. */
  private fmtWhen(iso: string): string {
    if (!iso) return "—";
    const at = new Date(iso);
    if (Number.isNaN(at.getTime())) return iso;
    const minutes = Math.floor((Date.now() - at.getTime()) / 60_000);
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return this.fmtDate(iso);
  }

  private fmtExact(iso: string): string {
    if (!iso) return "";
    const at = new Date(iso);
    return Number.isNaN(at.getTime()) ? iso : at.toLocaleString();
  }

  /** failureText renders an [ApiError] for a toast, with the request id appended
   * when there is one to quote. */
  private failureText(err: ApiError): string {
    return err.requestId ? `${err.message} (reference ${err.requestId})` : err.message;
  }

  // ── Render helpers ──────────────────────────────────────────────────────

  /**
   * renderFailure is the error state every card on this page shares: the
   * product's copy for the catalogue code, the code and the request id as
   * copyable rows, and a retry. `OD-11` puts `request_id` on every response
   * precisely so a reader can quote one, so it is surfaced rather than logged.
   */
  private renderFailure(err: ApiError, retry: () => void) {
    const rows: BnKvRow[] = [{ label: "Error code", value: err.code }];
    if (err.requestId) rows.push({ label: "Reference", value: err.requestId });
    return html`<div class="fail">
      <p class="fail-message" role="alert">${err.message}</p>
      <bn-kv
        .rows=${rows}
        @bn-copy=${(e: CustomEvent<{ label: string }>) => toast(`${e.detail.label} copied`)}
      ></bn-kv>
      ${err.requestId
        ? nothing
        : html`<p class="copy">
            This request never reached Sluice Cloud, so there is no reference to quote.
          </p>`}
      <div class="fail-actions">
        <bn-button variant="secondary" size="sm" @click=${retry}>Try again</bn-button>
      </div>
    </div>`;
  }

  private renderPasskeyTable() {
    const columns: BnColumn[] = [
      { key: "name", label: "Name" },
      { key: "kind", label: "Type", width: "1px" },
      { key: "created", label: "Added", width: "1px" },
      { key: "lastUsed", label: "Last used", width: "1px" },
      { key: "actions", label: "", align: "right", width: "1px" },
    ];
    const rows: BnRow[] = this.passkeys.map((p) => ({
      name: p.name,
      // The WebAuthn backup-eligibility flag, in plain words: "Synced" lives in
      // a password manager and works on the user's other devices, "This device
      // only" does not and is lost with the device.
      kind: p.backedUp
        ? html`<bn-badge variant="info">Synced</bn-badge>`
        : html`<bn-badge variant="neutral">This device only</bn-badge>`,
      created: this.fmtDate(p.created),
      lastUsed: p.lastUsed
        ? this.fmtWhen(p.lastUsed)
        : html`<bn-badge variant="neutral">Never used</bn-badge>`,
      actions: html`<span class="row-actions">
        <bn-button
          variant="secondary"
          size="sm"
          aria-label=${`Rename passkey ${p.name}`}
          @click=${() => this.openRename(p)}
          >Rename</bn-button
        >
        <bn-button
          variant="danger"
          size="sm"
          aria-label=${`Remove passkey ${p.name}`}
          @click=${() => (this.removeTarget = p)}
          >Remove</bn-button
        >
      </span>`,
    }));
    return html`<bn-table
      caption="Registered passkeys"
      .columns=${columns}
      .rows=${rows}
    ></bn-table>`;
  }

  private renderPasskeys() {
    if (this.pkLoading) {
      return html`<bn-card flush heading="Passkeys">
        <bn-spinner block label="Loading passkeys…"></bn-spinner>
      </bn-card>`;
    }
    if (this.pkError) {
      return html`<bn-card flush heading="Passkeys">
        ${this.renderFailure(this.pkError, () => void this.loadPasskeys())}
      </bn-card>`;
    }
    if (this.passkeys.length === 0) {
      return html`<bn-card flush heading="Passkeys">
        <bn-empty-state
          icon="◈"
          heading="No passkeys yet"
          message=${this.passkeySupport
            ? "Add a passkey to sign in with your fingerprint, face or device PIN — no email code, no password."
            : "This browser cannot use passkeys. Sign in with an emailed one-time code, or open Sluice Cloud in a browser with WebAuthn support over HTTPS."}
        >
          ${this.passkeySupport
            ? html`<bn-button slot="actions" variant="primary" @click=${() => this.openAdd()}
                >Add a passkey</bn-button
              >`
            : nothing}
        </bn-empty-state>
      </bn-card>`;
    }
    return html`<bn-card flush heading="Passkeys">
      ${this.passkeySupport
        ? html`<bn-button slot="actions" variant="primary" size="sm" @click=${() => this.openAdd()}
            >Add a passkey</bn-button
          >`
        : nothing}
      ${this.renderPasskeyTable()}
      <p class="note">
        A passkey signs you in without typing an email address. Removing every passkey does not lock
        you out — an emailed one-time code always works.
      </p>
    </bn-card>`;
  }

  private renderEventTable() {
    const columns: BnColumn[] = [
      { key: "event", label: "Event" },
      { key: "method", label: "Method", width: "1px" },
      { key: "ip", label: "From", mono: true, width: "1px" },
      { key: "device", label: "Device" },
      { key: "when", label: "When", align: "right", width: "1px" },
    ];
    const rows: BnRow[] = this.events.map((ev) => ({
      // `SC-S013`: an event type this bundle does not know renders as the raw
      // string, so a new server-side type never needs a client release.
      event: html`<span class="cell-event"
        >${EVENT_LABELS[ev.event] ?? ev.event}${ev.ok
          ? nothing
          : html`<bn-badge variant="error">Failed</bn-badge>`}</span
      >`,
      method: ev.method ? (METHOD_LABELS[ev.method] ?? ev.method) : "—",
      ip: ev.ip || "—",
      device: ev.user_agent
        ? html`<span class="ua" title=${ev.user_agent}>${ev.user_agent}</span>`
        : "—",
      when: html`<span title=${this.fmtExact(ev.created)}>${this.fmtWhen(ev.created)}</span>`,
    }));
    return html`<bn-table
      caption="Sign-in and security history"
      .columns=${columns}
      .rows=${rows}
    ></bn-table>`;
  }

  private renderHistory() {
    if (this.evLoading) {
      return html`<bn-card flush heading="Sign-in and security history">
        <bn-spinner block label="Loading security history…"></bn-spinner>
      </bn-card>`;
    }
    if (this.evError) {
      return html`<bn-card flush heading="Sign-in and security history">
        ${this.renderFailure(this.evError, () => void this.loadEvents())}
      </bn-card>`;
    }
    if (this.events.length === 0) {
      return html`<bn-card flush heading="Sign-in and security history">
        <bn-empty-state
          icon="≡"
          heading="Nothing recorded yet"
          message="Sign-ins, passkey changes and API key activity on your account appear here as they happen."
        ></bn-empty-state>
      </bn-card>`;
    }
    return html`<bn-card flush heading="Sign-in and security history">
      ${this.renderEventTable()}
      ${this.evNext !== null
        ? html`<div class="foot">
            <bn-button
              variant="secondary"
              size="sm"
              ?loading=${this.evMore}
              @click=${() => void this.loadMoreEvents()}
              >Load older events</bn-button
            >
          </div>`
        : nothing}
      <p class="note">
        This is a record of what happened, not of what was used to do it: it never contains a
        one-time code, a session token or API key material.
      </p>
    </bn-card>`;
  }

  private renderSignOutCard() {
    return html`<bn-card heading="Sign out everywhere">
      <p class="copy">
        Ends every signed-in session for your account — other browsers, other devices, and this one.
        Use it if a device is lost or you signed in somewhere you do not control.
      </p>
      <p class="copy">
        It works by rotating your session key, so it is <strong>all or nothing</strong>: signing one
        other device out on its own is not something the session model can express.
      </p>
      <bn-button variant="danger" @click=${() => this.openSignOut()}>Sign out everywhere</bn-button>
    </bn-card>`;
  }

  private renderSignOutModal() {
    const keyCount = this.keys.length;
    return html`<bn-modal
      ?open=${this.signOutOpen}
      tone="destructive"
      heading="Sign out everywhere?"
      width=${560}
      @close=${() => this.closeSignOut()}
    >
      <p class="copy">
        Every device signed in to your account is signed out, <strong>including this one</strong>.
        You will need to sign in again here.
      </p>
      ${this.keysLoading
        ? html`<bn-spinner label="Checking your API keys…"></bn-spinner>`
        : this.keysError
          ? html`<p class="copy">
              Your API keys could not be listed (${this.keysError.code}), so this dialog cannot
              offer to revoke them. Signing out everywhere still works, and the keys page can revoke
              them afterwards.
            </p>`
          : keyCount === 0
            ? html`<p class="copy">You hold no API keys, so there is nothing else to revoke.</p>`
            : html`
                <p class="copy">
                  These ${keyCount === 1 ? "key keeps" : `${keyCount} keys keep`} working — their
                  hashes live in their own table, and only tokens already handed out are killed:
                </p>
                <ul class="keys-list">
                  ${this.keys.map(
                    (k) => html`<li>${k.name} · <code>${k.display_prefix}</code></li>`,
                  )}
                </ul>
                <bn-checkbox
                  label=${`Also revoke ${keyCount === 1 ? "this API key" : `all ${keyCount} API keys`}`}
                  hint="Revoked immediately and permanently. Anything using them — CI, scripts, the CLI — stops working on its next request."
                  .checked=${this.alsoRevokeKeys}
                  ?disabled=${this.signingOut}
                  @change=${(e: CustomEvent<{ checked: boolean }>) =>
                    (this.alsoRevokeKeys = e.detail.checked)}
                ></bn-checkbox>
              `}
      <p slot="consequence">
        Outstanding step-up verifications expire within five minutes on their own. Credentials that
        belong to an instance rather than to you are untouched — a human signing out must never take
        a running gateway offline.
      </p>
      <bn-button slot="footer" variant="ghost" @click=${() => this.closeSignOut()}>Cancel</bn-button>
      <bn-button
        slot="footer"
        variant="danger"
        ?loading=${this.signingOut}
        @click=${() => void this.confirmSignOut()}
        >${this.alsoRevokeKeys ? "Revoke keys and sign out" : "Sign out everywhere"}</bn-button
      >
    </bn-modal>`;
  }

  private renderAddModal() {
    const tooLong = this.addName.trim().length > NAME_LIMIT;
    return html`<bn-modal
      ?open=${this.addOpen}
      heading="Add a passkey"
      @close=${() => this.closeAdd()}
    >
      <bn-field label="Passkey name" required hint="A label to recognise it later.">
        <bn-input
          .value=${this.addName}
          autocomplete="off"
          ?disabled=${this.adding}
          diagnostic=${tooLong ? `Passkey names are limited to ${NAME_LIMIT} characters.` : ""}
          @input=${(e: CustomEvent<{ value: string }>) => (this.addName = e.detail.value)}
        ></bn-input>
      </bn-field>
      <p class="copy">
        Your browser will ask you to confirm with your fingerprint, face or device PIN. The passkey
        never leaves your device or your password manager — Sluice Cloud stores only its public key.
      </p>
      <bn-button slot="footer" variant="ghost" @click=${() => this.closeAdd()}>Cancel</bn-button>
      <bn-button
        slot="footer"
        variant="primary"
        ?loading=${this.adding}
        ?disabled=${this.addName.trim() === "" || tooLong}
        @click=${() => void this.submitAdd()}
        >Create passkey</bn-button
      >
    </bn-modal>`;
  }

  private renderRenameModal() {
    const tooLong = this.renameName.trim().length > NAME_LIMIT;
    return html`<bn-modal
      ?open=${this.renameTarget !== null}
      heading="Rename passkey"
      @close=${() => this.closeRename()}
    >
      <bn-field label="Passkey name" required>
        <bn-input
          .value=${this.renameName}
          autocomplete="off"
          ?disabled=${this.renaming}
          diagnostic=${tooLong ? `Passkey names are limited to ${NAME_LIMIT} characters.` : ""}
          @input=${(e: CustomEvent<{ value: string }>) => (this.renameName = e.detail.value)}
        ></bn-input>
      </bn-field>
      <bn-button slot="footer" variant="ghost" @click=${() => this.closeRename()}>Cancel</bn-button>
      <bn-button
        slot="footer"
        variant="primary"
        ?loading=${this.renaming}
        ?disabled=${this.renameName.trim() === "" || tooLong}
        @click=${() => void this.submitRename()}
        >Rename</bn-button
      >
    </bn-modal>`;
  }

  private renderRemoveModal() {
    const target = this.removeTarget;
    const last = this.passkeys.length === 1;
    return html`<bn-modal
      ?open=${target !== null}
      tone="destructive"
      heading="Remove passkey?"
      @close=${() => this.closeRemove()}
    >
      ${target
        ? html`<p class="copy">
            <span class="name">${target.name}</span> stops working for sign-in immediately, and the
            credential becomes free to register again.
          </p>`
        : nothing}
      <p slot="consequence">
        ${last
          ? "This is your last passkey. Removing it is allowed by design: an emailed one-time code always works, so you cannot lock yourself out."
          : "You can still sign in with your other passkeys, or with an emailed one-time code."}
      </p>
      <bn-button slot="footer" variant="ghost" @click=${() => this.closeRemove()}>Cancel</bn-button>
      <bn-button
        slot="footer"
        variant="danger"
        ?loading=${this.removing}
        @click=${() => void this.confirmRemove()}
        >Remove passkey</bn-button
      >
    </bn-modal>`;
  }

  override render() {
    return html`
      <h1>Security</h1>
      <p class="subtitle">
        How you sign in to Sluice Cloud, and what has happened on your account. Nothing on this page
        can be reached by an API key of any kind — managing your own authentication factors is the
        one thing automation is never allowed to do.
      </p>

      <div class="stack">
        ${this.renderPasskeys()} ${this.renderHistory()} ${this.renderSignOutCard()}
      </div>

      ${this.renderAddModal()} ${this.renderRenameModal()} ${this.renderRemoveModal()}
      ${this.renderSignOutModal()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-security": PageSecurity;
  }
}
